import React, { useState, useEffect, useContext } from 'react';
import UserContext from '../../../context/UserContext.js';
import FilaPartidoFixture from './FilaPartidoFixture.js';
import ColumnaBotoneraItem from '../../edicion/ColumnaBotoneraItem.js';
import MensajeCargando from '../../UI/MensajeCargando.js';
import MensajeError from '../../UI/MensajeError.js';
import MensajeGrabando from '../../UI/MensajeGrabando.js';
import useGetListaURL from '../../../hooks/useGetListaURL.js';
import usePostURL from '../../../hooks/usePostURL.js';
import usePatchURL from '../../../hooks/usePatchURL.js';
import api from '../../../api/client.js';
import * as Instancia from '../../constantes/InstanciaPartido.js';
import * as Funciones from '../../funciones/FuncionesFixture.js';
import * as FuncionesFecha from '../../funciones/FuncionesFecha.js';
import * as FuncionesEquipo from '../../funciones/FuncionesEquipo.js';
import * as DTOs from '../../DTOs/PartidoFixtureDTOs.js';
import styles from './FixtureEtapaDefinicion.module.css';

/**
 * Componente que gestiona la carga del fixture para una etapa de definición
 * Muestra partidos existentes y filas editables para crear los faltantes
 */
const FixtureEtapaDefinicion = (props) => {
  const datosElegidos = props.datosElegidos; // { torneo, categoria, etapa }
  const etapa = datosElegidos?.etapa;
  const torneo = datosElegidos?.torneo;
  const { usuario } = useContext(UserContext);
  const puedeEditar = usuario && usuario.puedeEditar;

  const [partidosExistentes, setPartidosExistentes] = useState([]);
  const [partidosFaltantes, setPartidosFaltantes] = useState([]);
  const [equiposDisponibles, setEquiposDisponibles] = useState([]);
  const [partidosEnEdicion, setPartidosEnEdicion] = useState({}); // { idPartido: partidoEditado }
  const [partidoGrabandose, setPartidoGrabandose] = useState(null); // idPartido del partido que se está grabando
  const [erroresFilaPartido, setErroresFilaPartido] = useState({});

  const { isCargando, erroresGetLista, fetchData, resetErroresGetLista } = useGetListaURL();
  const { isGrabando, erroresPost, postData, resetErroresPost } = usePostURL();
  const { isGrabando: isGrabandoPatch, erroresPatch, patchData, resetErroresPatch } = usePatchURL();

  // Mapear instancia descripción a código (backend devuelve "Unico"/"Ida"/"Vuelta" pero el código espera "U"/"I"/"V")
  const mapearInstanciaACodigo = (instancia) => {
    if (!instancia) return null;
    const desc = instancia.toLowerCase();
    if (desc === 'unico') return Instancia.UNICO;
    if (desc === 'ida') return Instancia.IDA;
    if (desc === 'vuelta') return Instancia.VUELTA;
    return instancia; // por si ya viene como código
  };

  // Cargar partidos existentes de la etapa
  useEffect(() => {
    resetErroresGetLista();
    if (torneo && etapa && etapa.etapa) {
      const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
      fetchData(url, (partidos) => {
        // Mapear instancia de descripción a código
        const partidosMapeados = (partidos || []).map((p) => ({
          ...p,
          codigoInstancia: mapearInstanciaACodigo(p.instancia),
        }));
        setPartidosExistentes(partidosMapeados);
      });
    } else {
      setPartidosExistentes([]);
    }
  }, [torneo, etapa, fetchData, resetErroresGetLista]);

  // Limpiar partidoGrabandose cuando hay errores
  useEffect(() => {
    if (erroresPost?.length > 0 || erroresPatch?.length > 0) {
      setPartidoGrabandose(null);
      if (partidoGrabandose != null) {
        setErroresFilaPartido((prev) => ({
          ...prev,
          [partidoGrabandose]: erroresPost?.length > 0 ? erroresPost : erroresPatch || [],
        }));
      }
    }
  }, [erroresPost, erroresPatch, partidoGrabandose]);

  // Cargar equipos disponibles (participantes del torneo)
  useEffect(() => {
    if (torneo) {
      fetchData(`torneos/${torneo.id}/participantes`, (participantes) => {
        const equipos = participantes.map((p) => p.equipo).filter((e) => e != null);
        setEquiposDisponibles(equipos);
      });
    } else {
      setEquiposDisponibles([]);
    }
  }, [torneo, fetchData]);

  // Calcular partidos faltantes
  useEffect(() => {
    if (etapa && datosElegidos) {
      const faltantes = Funciones.PartidosFaltantesDefinicion(partidosExistentes, datosElegidos);
      setPartidosFaltantes(faltantes);
    } else {
      setPartidosFaltantes([]);
    }
  }, [partidosExistentes, etapa, datosElegidos]);

  const onPartidoChange = (index, partidoPatch) => {
    const nuevosPartidos = [...partidosFaltantes];
    const partidoActual = nuevosPartidos[index] || {};
    nuevosPartidos[index] = {
      ...partidoActual,
      ...partidoPatch,
    };

    // Si es ida y el partido tiene cantidadRuedas=2, replicar a vuelta
    if (etapa.cantidadRuedas === 2 && nuevosPartidos[index].codigoInstancia === Instancia.IDA) {
      // Buscar el partido de vuelta correspondiente por indiceCruce
      const indexVuelta = nuevosPartidos.findIndex(
        (p) =>
          p.codigoInstancia === Instancia.VUELTA &&
          p.indiceCruce === nuevosPartidos[index].indiceCruce
      );

      if (indexVuelta !== -1) {
        let partidoVuelta = nuevosPartidos[indexVuelta];

        // Replicar equipos invertidos
        if (nuevosPartidos[index].equipoLocal) {
          partidoVuelta = Funciones.ReplicarEquipoIdaVuelta(
            nuevosPartidos[index],
            partidoVuelta,
            'equipoLocal'
          );
        }
        if (nuevosPartidos[index].equipoVisitante) {
          partidoVuelta = Funciones.ReplicarEquipoIdaVuelta(
            nuevosPartidos[index],
            partidoVuelta,
            'equipoVisitante'
          );
        }

        nuevosPartidos[indexVuelta] = partidoVuelta;
      }
    }

    setPartidosFaltantes(nuevosPartidos);
  };

  const onGuardarPartido = async (partido) => {
    // Validar que tenga lo mínimo necesario
    if (!partido.anioPartido) {
      alert('Debe ingresar la fecha del partido');
      return;
    }
    if (!partido.equipoLocal) {
      alert('Debe seleccionar el equipo local');
      return;
    }
    if (!partido.equipoVisitante) {
      alert('Debe seleccionar el equipo visitante');
      return;
    }

    // Validar que local != visitante
    if (partido.equipoLocal.id === partido.equipoVisitante.id) {
      alert('Error: El equipo local y visitante no pueden ser el mismo.');
      return;
    }

    // Validar que no se repitan equipos en otros partidos de IDA de la misma etapa
    if (partido.codigoInstancia === Instancia.IDA || partido.codigoInstancia === Instancia.UNICO) {
      // Verificar contra otros partidos faltantes (nuevos)
      const otrosPartidosIda = partidosFaltantes.filter(
        (p) =>
          p.idPartido !== partido.idPartido &&
          p.codigoInstancia === partido.codigoInstancia &&
          (p.equipoLocal || p.equipoVisitante)
      );

      for (const otroPartido of otrosPartidosIda) {
        if (
          otroPartido.equipoLocal?.id === partido.equipoLocal?.id ||
          otroPartido.equipoLocal?.id === partido.equipoVisitante?.id ||
          otroPartido.equipoVisitante?.id === partido.equipoLocal?.id ||
          otroPartido.equipoVisitante?.id === partido.equipoVisitante?.id
        ) {
          alert('Error: Uno de los equipos ya está asignado a otro partido de esta instancia.');
          return;
        }
      }

      // Verificar contra partidos existentes
      const partidosExistentesInstancia = partidosExistentes.filter(
        (p) => p.codigoInstancia === partido.codigoInstancia
      );

      for (const otroPartido of partidosExistentesInstancia) {
        if (
          otroPartido.equipoLocal?.id === partido.equipoLocal?.id ||
          otroPartido.equipoLocal?.id === partido.equipoVisitante?.id ||
          otroPartido.equipoVisitante?.id === partido.equipoLocal?.id ||
          otroPartido.equipoVisitante?.id === partido.equipoVisitante?.id
        ) {
          alert('Error: Uno de los equipos ya está asignado a otro partido de esta instancia.');
          return;
        }
      }
    }

    resetErroresPost();
    setErroresFilaPartido((prev) => {
      const nuevos = { ...prev };
      delete nuevos[partido.idPartido];
      return nuevos;
    });

    setPartidoGrabandose(partido.idPartido); // Marcar que este partido se está grabando

    // Si es un partido de IDA (nuevo), SIEMPRE crear también el partido de VUELTA
    if (partido.codigoInstancia === Instancia.IDA && etapa.cantidadRuedas === 2) {
      // Buscar el partido de VUELTA correspondiente
      let partidoVuelta = partidosFaltantes.find(
        (p) => p.codigoInstancia === Instancia.VUELTA && p.indiceCruce === partido.indiceCruce
      );

      if (partidoVuelta) {
        // Copiar equipos invertidos del IDA (la fecha NO se copia porque son días diferentes)
        if (!partidoVuelta.equipoLocal || !partidoVuelta.equipoVisitante) {
          partidoVuelta = {
            ...partidoVuelta,
            equipoLocal: partido.equipoVisitante,
            equipoVisitante: partido.equipoLocal,
          };
        }

        // Grabar IDA + VUELTA juntos usando batch endpoint
        const dtos = [DTOs.SavePartidoFixture(partido), DTOs.SavePartidoFixture(partidoVuelta)];

        // Guardar datos para identificar el partido de VUELTA después de recargar
        const equiposIda = {
          local: partido.equipoLocal,
          visitante: partido.equipoVisitante,
        };
        const vueltatieneSoloEquipos = !partidoVuelta.anioPartido;

        postData(`torneos/${torneo.id}/partidos`, dtos, (resultado) => {
          setPartidoGrabandose(null); // Limpiar estado de grabando
          setErroresFilaPartido((prev) => {
            const nuevos = { ...prev };
            delete nuevos[partido.idPartido];
            return nuevos;
          });
          setPartidosFaltantes((prev) =>
            prev.filter(
              (p) => p.idPartido !== partido.idPartido && p.idPartido !== partidoVuelta.idPartido
            )
          );
          const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
          fetchData(url, (partidos) => {
            const partidosMapeados = (partidos || []).map((p) => ({
              ...p,
              codigoInstancia: mapearInstanciaACodigo(p.instancia),
            }));
            setPartidosExistentes(partidosMapeados);

            // Si el partido de VUELTA solo tenía equipos, ponerlo en modo edición
            if (vueltatieneSoloEquipos) {
              const partidoVueltaCreado = partidosMapeados.find(
                (p) =>
                  p.codigoInstancia === Instancia.VUELTA &&
                  p.equipoLocal?.id === equiposIda.visitante?.id &&
                  p.equipoVisitante?.id === equiposIda.local?.id &&
                  !p.anioPartido && // Sin fecha cargada
                  !p.resultadoLocal &&
                  !p.resultadoVisitante // Sin resultado
              );

              if (partidoVueltaCreado) {
                setPartidosEnEdicion({ [partidoVueltaCreado.id]: { ...partidoVueltaCreado } });
              }
            }
          });
        });
        return;
      }
    }

    // Para partidos VUELTA o UNICO, grabar usando batch endpoint
    const dto = DTOs.SavePartidoFixture(partido);
    postData(`torneos/${torneo.id}/partidos`, [dto], (resultado) => {
      setPartidoGrabandose(null); // Limpiar estado de grabando
      setPartidosFaltantes((prev) => prev.filter((p) => p.idPartido !== partido.idPartido));
      const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
      fetchData(url, (partidos) => {
        const partidosMapeados = (partidos || []).map((p) => ({
          ...p,
          codigoInstancia: mapearInstanciaACodigo(p.instancia),
        }));
        setPartidosExistentes(partidosMapeados);
      });
    });
  };

  const onCancelarPartido = (partido) => {
    // Limpiar el partido (volver a valores iniciales)
    const contexto = datosElegidos;
    const nuevoPartido = DTOs.PartidoFixtureVacio(
      contexto,
      partido.idPartido,
      partido.codigoInstancia,
      partido.indiceCruce,
      partido.zona,
      partido.nroFecha
    );

    const indexEnFaltantes = partidosFaltantes.findIndex((p) => p.idPartido === partido.idPartido);

    if (indexEnFaltantes !== -1) {
      const nuevosPartidos = [...partidosFaltantes];
      nuevosPartidos[indexEnFaltantes] = nuevoPartido;
      setPartidosFaltantes(nuevosPartidos);
    }
  };

  const onEditarPartido = (idPartido) => {
    const partido = partidosExistentes.find((p) => p.id === idPartido);
    if (partido) {
      const golesLocalOriginal = partido.golesLocal ?? partido.resultadoLocal ?? null;
      const golesVisitanteOriginal = partido.golesVisitante ?? partido.resultadoVisitante ?? null;
      setPartidosEnEdicion({
        ...partidosEnEdicion,
        [idPartido]: {
          ...partido,
          golesLocalOriginal,
          golesVisitanteOriginal,
          localEditado: false,
          visitanteEditado: false,
        },
      });
    }
  };

  const onCancelarEdicion = (idPartido) => {
    const nuevosEnEdicion = { ...partidosEnEdicion };
    delete nuevosEnEdicion[idPartido];
    setPartidosEnEdicion(nuevosEnEdicion);
  };

  const onPartidoEnEdicionChange = (idPartido, partidoPatch) => {
    setPartidosEnEdicion((prev) => ({
      ...prev,
      [idPartido]: {
        ...(prev[idPartido] || {}),
        ...partidoPatch,
      },
    }));
  };

  const onGuardarEdicion = async (partido) => {
    // Validar
    if (!partido.anioPartido || !partido.equipoLocal || !partido.equipoVisitante) {
      alert('Debe completar los datos del partido');
      return;
    }

    // Validar que local != visitante
    if (partido.equipoLocal.id === partido.equipoVisitante.id) {
      alert('Error: El equipo local y visitante no pueden ser el mismo.');
      return;
    }

    // Validar que no se repitan equipos en otros partidos de la misma instancia
    if (partido.codigoInstancia === Instancia.IDA || partido.codigoInstancia === Instancia.UNICO) {
      // Verificar contra otros partidos existentes
      const otrosPartidosInstancia = partidosExistentes.filter(
        (p) => p.id !== partido.id && p.codigoInstancia === partido.codigoInstancia
      );

      for (const otroPartido of otrosPartidosInstancia) {
        if (
          otroPartido.equipoLocal?.id === partido.equipoLocal?.id ||
          otroPartido.equipoLocal?.id === partido.equipoVisitante?.id ||
          otroPartido.equipoVisitante?.id === partido.equipoLocal?.id ||
          otroPartido.equipoVisitante?.id === partido.equipoVisitante?.id
        ) {
          alert('Error: Uno de los equipos ya está asignado a otro partido de esta instancia.');
          return;
        }
      }

      // Verificar contra partidos faltantes (nuevos) que tienen equipos asignados
      const partidosFaltantesInstancia = partidosFaltantes.filter(
        (p) => p.codigoInstancia === partido.codigoInstancia && (p.equipoLocal || p.equipoVisitante)
      );

      for (const otroPartido of partidosFaltantesInstancia) {
        if (
          otroPartido.equipoLocal?.id === partido.equipoLocal?.id ||
          otroPartido.equipoLocal?.id === partido.equipoVisitante?.id ||
          otroPartido.equipoVisitante?.id === partido.equipoLocal?.id ||
          otroPartido.equipoVisitante?.id === partido.equipoVisitante?.id
        ) {
          alert('Error: Uno de los equipos ya está asignado a otro partido de esta instancia.');
          return;
        }
      }
    }

    resetErroresPatch();

    setPartidoGrabandose(partido.id); // Marcar que este partido se está grabando

    // Si es un partido de IDA existente, verificar si se modificaron los equipos
    if (partido.codigoInstancia === Instancia.IDA && etapa.cantidadRuedas === 2) {
      const partidoOriginal = partidosExistentes.find((p) => p.id === partido.id);
      const equiposModificados =
        partidoOriginal &&
        (partidoOriginal.equipoLocal?.id !== partido.equipoLocal?.id ||
          partidoOriginal.equipoVisitante?.id !== partido.equipoVisitante?.id);

      if (equiposModificados) {
        const confirmar = window.confirm(
          'ATENCIÓN: Al modificar los equipos del partido de IDA, también se modificarán los equipos del partido de VUELTA correspondiente. ¿Desea continuar?'
        );
        if (!confirmar) {
          return;
        }

        // Buscar el partido de VUELTA correspondiente
        // Estrategia 1: Mismo ordenPartido (si existe y coincide)
        let partidoVuelta = partidosExistentes.find(
          (p) =>
            p.codigoInstancia === Instancia.VUELTA &&
            p.ordenPartido &&
            partido.ordenPartido &&
            p.ordenPartido === partido.ordenPartido
        );

        // Estrategia 2: Si no se encontró por ordenPartido, buscar por equipos invertidos
        if (!partidoVuelta) {
          partidoVuelta = partidosExistentes.find(
            (p) =>
              p.codigoInstancia === Instancia.VUELTA &&
              p.equipoLocal?.id === partidoOriginal.equipoVisitante?.id &&
              p.equipoVisitante?.id === partidoOriginal.equipoLocal?.id
          );
        }

        if (partidoVuelta) {
          // Si el partido de VUELTA está en edición, usar esa versión; sino usar el original
          const partidoVueltaBase = partidosEnEdicion[partidoVuelta.id] || partidoVuelta;

          // Actualizar ambos partidos usando batch endpoint
          const dtoIda = {
            idPartido: partido.id,
            ...DTOs.PatchPartidoFixture(partido),
          };
          const dtoVuelta = {
            idPartido: partidoVueltaBase.id,
            ...DTOs.PatchPartidoFixture({
              ...partidoVueltaBase,
              equipoLocal: partido.equipoVisitante,
              equipoVisitante: partido.equipoLocal,
            }),
          };

          patchData(`torneos/${torneo.id}/partidos`, [dtoIda, dtoVuelta], () => {
            setPartidoGrabandose(null); // Limpiar estado de grabando
            // Recargar lista
            recargarPartidos();
            // Salir del modo edición para ambos partidos (IDA y VUELTA)
            setPartidosEnEdicion((prev) => {
              const nuevosEnEdicion = { ...prev };
              delete nuevosEnEdicion[partido.id];
              delete nuevosEnEdicion[partidoVueltaBase.id];
              return nuevosEnEdicion;
            });
          });
          return; // Salir después de actualizar ambos
        } else {
          console.warn(
            'No se encontró el partido de VUELTA correspondiente. Solo se actualizará el partido de IDA.',
            { partidoIda: partido.id, ordenPartido: partido.ordenPartido }
          );
          alert(
            'ADVERTENCIA: No se encontró el partido de VUELTA correspondiente. Solo se actualizará el partido de IDA. Verifique los datos.'
          );
        }
      }
    }

    // Para otros casos (sin modificación de equipos, o VUELTA, o UNICO)
    const dto = DTOs.PatchPartidoFixture(partido);
    patchData(`partidos/${partido.id}`, dto, () => {
      setPartidoGrabandose(null); // Limpiar estado de grabando
      // Recargar lista
      const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
      fetchData(url, (partidos) => {
        const partidosMapeados = (partidos || []).map((p) => ({
          ...p,
          codigoInstancia: mapearInstanciaACodigo(p.instancia),
        }));
        setPartidosExistentes(partidosMapeados);
      });
      // Salir del modo edición
      onCancelarEdicion(partido.id);
    });
  };

  const onEliminarPartido = async (idPartido) => {
    const confirmar = window.confirm('¿Está seguro que desea eliminar este partido?');
    if (!confirmar) return;

    try {
      const response = await api.delete(`partidos/${idPartido}`);
      if (response.ok) {
        // Recargar lista
        const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
        fetchData(url, (partidos) => {
          const partidosMapeados = (partidos || []).map((p) => ({
            ...p,
            codigoInstancia: mapearInstanciaACodigo(p.instancia),
          }));
          setPartidosExistentes(partidosMapeados);
        });
      } else {
        alert(`Error al eliminar partido: ${response.errors?.join(', ') || 'Error desconocido'}`);
      }
    } catch (error) {
      alert(`Error al eliminar partido: ${error.message}`);
    }
  };

  const onGuardarTodos = async () => {
    // Validar partidos faltantes (nuevos)
    let partidosNuevosValidos = partidosFaltantes.filter((p) => {
      return p.anioPartido && p.equipoLocal && p.equipoVisitante;
    });

    // Validación 1: Equipo local != equipo visitante
    const partidosConEquiposIguales = partidosNuevosValidos.filter(
      (p) => p.equipoLocal?.id === p.equipoVisitante?.id
    );
    if (partidosConEquiposIguales.length > 0) {
      alert('Error: El equipo local y visitante no pueden ser el mismo en un partido.');
      return;
    }

    // Validación 2: No repetir equipos en partidos de IDA
    const partidosIdaParaValidar = partidosNuevosValidos.filter(
      (p) => p.codigoInstancia === Instancia.IDA
    );
    const equiposEnIda = [];
    for (const partido of partidosIdaParaValidar) {
      if (equiposEnIda.includes(partido.equipoLocal?.id)) {
        alert(
          `Error: El equipo ${partido.equipoLocal?.nombre} aparece en más de un partido de IDA.`
        );
        return;
      }
      if (equiposEnIda.includes(partido.equipoVisitante?.id)) {
        alert(
          `Error: El equipo ${partido.equipoVisitante?.nombre} aparece en más de un partido de IDA.`
        );
        return;
      }
      equiposEnIda.push(partido.equipoLocal?.id);
      equiposEnIda.push(partido.equipoVisitante?.id);
    }

    // Si hay etapa ida y vuelta, agregar partidos de VUELTA automáticamente cuando se crea IDA
    if (etapa.cantidadRuedas === 2) {
      const partidosIdaValidos = partidosNuevosValidos.filter(
        (p) => p.codigoInstancia === Instancia.IDA
      );

      // Para cada partido de IDA, verificar si hay VUELTA correspondiente
      partidosIdaValidos.forEach((partidoIda) => {
        // Verificar directamente en partidosNuevosValidos para capturar los agregados en iteraciones previas
        const tieneVuelta = partidosNuevosValidos.some(
          (pv) =>
            pv.codigoInstancia === Instancia.VUELTA && pv.indiceCruce === partidoIda.indiceCruce
        );
        if (!tieneVuelta) {
          // Buscar el partido de VUELTA en partidosFaltantes
          let partidoVuelta = partidosFaltantes.find(
            (p) =>
              p.codigoInstancia === Instancia.VUELTA && p.indiceCruce === partidoIda.indiceCruce
          );
          if (partidoVuelta) {
            // SIEMPRE copiar equipos invertidos desde el partido de IDA (la fecha NO se copia)
            partidoVuelta = {
              ...partidoVuelta,
              equipoLocal: partidoIda.equipoVisitante,
              equipoVisitante: partidoIda.equipoLocal,
            };
            // Agregar a la lista de nuevos válidos
            partidosNuevosValidos.push(partidoVuelta);
          }
        }
      });
    }

    // Validar partidos en edición (existentes)
    let partidosEditadosValidos = Object.values(partidosEnEdicion).filter((p) => {
      return p && p.id && p.anioPartido && p.equipoLocal && p.equipoVisitante;
    });

    // Validación 3: Equipo local != equipo visitante en partidos editados
    const partidosEditadosConEquiposIguales = partidosEditadosValidos.filter(
      (p) => p.equipoLocal?.id === p.equipoVisitante?.id
    );
    if (partidosEditadosConEquiposIguales.length > 0) {
      alert('Error: El equipo local y visitante no pueden ser el mismo en un partido.');
      return;
    }

    // Validación 4: No repetir equipos en partidos de IDA editados
    const partidosIdaEditados = partidosEditadosValidos.filter(
      (p) => p.codigoInstancia === Instancia.IDA
    );
    const equiposEnIdaEditados = [...equiposEnIda]; // Reusar la lista de partidos nuevos
    for (const partido of partidosIdaEditados) {
      if (equiposEnIdaEditados.includes(partido.equipoLocal?.id)) {
        alert(
          `Error: El equipo ${partido.equipoLocal?.nombre} aparece en más de un partido de IDA.`
        );
        return;
      }
      if (equiposEnIdaEditados.includes(partido.equipoVisitante?.id)) {
        alert(
          `Error: El equipo ${partido.equipoVisitante?.nombre} aparece en más de un partido de IDA.`
        );
        return;
      }
      equiposEnIdaEditados.push(partido.equipoLocal?.id);
      equiposEnIdaEditados.push(partido.equipoVisitante?.id);
    }

    // Validación 5: Verificar contra partidos existentes que NO están en edición
    const partidosExistentesNoEditados = partidosExistentes.filter(
      (p) =>
        !partidosEnEdicion[p.id] &&
        (p.codigoInstancia === Instancia.IDA || p.codigoInstancia === Instancia.UNICO)
    );
    for (const partidoExistente of partidosExistentesNoEditados) {
      if (equiposEnIdaEditados.includes(partidoExistente.equipoLocal?.id)) {
        alert(
          `Error: El equipo ${partidoExistente.equipoLocal?.nombre} ya está asignado a otro partido.`
        );
        return;
      }
      if (equiposEnIdaEditados.includes(partidoExistente.equipoVisitante?.id)) {
        alert(
          `Error: El equipo ${partidoExistente.equipoVisitante?.nombre} ya está asignado a otro partido.`
        );
        return;
      }
    }

    // Para partidos de IDA editados, verificar si se modificaron equipos y actualizar VUELTA
    if (etapa.cantidadRuedas === 2) {
      const partidosIdaEditados = partidosEditadosValidos.filter(
        (p) => p.codigoInstancia === Instancia.IDA
      );
      for (const partidoIda of partidosIdaEditados) {
        const partidoOriginal = partidosExistentes.find((p) => p.id === partidoIda.id);
        const equiposModificados =
          partidoOriginal &&
          (partidoOriginal.equipoLocal?.id !== partidoIda.equipoLocal?.id ||
            partidoOriginal.equipoVisitante?.id !== partidoIda.equipoVisitante?.id);

        if (equiposModificados) {
          // Buscar partido de VUELTA correspondiente
          // Estrategia 1: Mismo ordenPartido (si existe y coincide)
          let partidoVuelta = partidosExistentes.find(
            (p) =>
              p.codigoInstancia === Instancia.VUELTA &&
              p.ordenPartido &&
              partidoIda.ordenPartido &&
              p.ordenPartido === partidoIda.ordenPartido
          );

          // Estrategia 2: Si no se encontró por ordenPartido, buscar por equipos invertidos
          if (!partidoVuelta) {
            partidoVuelta = partidosExistentes.find(
              (p) =>
                p.codigoInstancia === Instancia.VUELTA &&
                p.equipoLocal?.id === partidoOriginal.equipoVisitante?.id &&
                p.equipoVisitante?.id === partidoOriginal.equipoLocal?.id
            );
          }

          if (partidoVuelta && !partidosEditadosValidos.find((p) => p.id === partidoVuelta.id)) {
            // Si el partido de VUELTA está en edición, usar esa versión; sino usar el original
            const partidoVueltaBase = partidosEnEdicion[partidoVuelta.id] || partidoVuelta;

            // Agregar partido de VUELTA con equipos invertidos a la lista de editados
            partidosEditadosValidos.push({
              ...partidoVueltaBase,
              equipoLocal: partidoIda.equipoVisitante,
              equipoVisitante: partidoIda.equipoLocal,
            });
          } else if (!partidoVuelta) {
            console.warn('No se encontró el partido de VUELTA correspondiente al partido de IDA.', {
              partidoIda: partidoIda.id,
              ordenPartido: partidoIda.ordenPartido,
            });
          }
        }
      }
    }

    const totalParaGuardar = partidosNuevosValidos.length + partidosEditadosValidos.length;

    if (totalParaGuardar === 0) {
      alert('No hay partidos válidos para guardar');
      return;
    }

    const mensaje = [];
    if (partidosNuevosValidos.length > 0) {
      mensaje.push(`crear ${partidosNuevosValidos.length} partido(s)`);
    }
    if (partidosEditadosValidos.length > 0) {
      mensaje.push(`actualizar ${partidosEditadosValidos.length} partido(s)`);
    }

    // Si hay equipos modificados en IDA, mostrar advertencia
    const hayModificacionEquipos =
      etapa.cantidadRuedas === 2 &&
      partidosEditadosValidos.some((p) => {
        const original = partidosExistentes.find((po) => po.id === p.id);
        return (
          p.codigoInstancia === Instancia.IDA &&
          original &&
          (original.equipoLocal?.id !== p.equipoLocal?.id ||
            original.equipoVisitante?.id !== p.equipoVisitante?.id)
        );
      });

    let mensajeConfirmacion = `¿Está seguro que desea ${mensaje.join(' y ')}?`;
    if (hayModificacionEquipos) {
      mensajeConfirmacion =
        'ATENCIÓN: Se modificaron equipos en partido(s) de IDA. También se actualizarán los equipos en los partidos de VUELTA correspondientes.\n\n' +
        mensajeConfirmacion;
    }

    const confirmar = window.confirm(mensajeConfirmacion);
    if (!confirmar) {
      return;
    }

    resetErroresPatch();

    // Crear nuevos partidos con POST batch
    if (partidosNuevosValidos.length > 0) {
      // Identificar partidos de VUELTA que solo tienen equipos (sin fecha) para ponerlos en edición después
      const partidosVueltaSinFecha = partidosNuevosValidos
        .filter((p) => p.codigoInstancia === Instancia.VUELTA && !p.anioPartido)
        .map((p) => ({
          localId: p.equipoLocal?.id,
          visitanteId: p.equipoVisitante?.id,
        }));

      const dtosNuevos = partidosNuevosValidos.map((partido) => DTOs.SavePartidoFixture(partido));
      postData(`torneos/${torneo.id}/partidos`, dtosNuevos, (resultado) => {
        // Eliminar los partidos creados de faltantes
        setPartidosFaltantes((prev) =>
          prev.filter((p) => !partidosNuevosValidos.some((pn) => pn.idPartido === p.idPartido))
        );
        // Recargar después de crear
        const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
        fetchData(url, (partidos) => {
          const partidosMapeados = (partidos || []).map((p) => ({
            ...p,
            codigoInstancia: mapearInstanciaACodigo(p.instancia),
          }));
          setPartidosExistentes(partidosMapeados);

          // Poner en modo edición los partidos de VUELTA recién creados que solo tienen equipos
          if (partidosVueltaSinFecha.length > 0) {
            const partidosEnEdicionNuevos = {};

            partidosVueltaSinFecha.forEach((vuelta) => {
              const partidoVueltaCreado = partidosMapeados.find(
                (p) =>
                  p.codigoInstancia === Instancia.VUELTA &&
                  p.equipoLocal?.id === vuelta.localId &&
                  p.equipoVisitante?.id === vuelta.visitanteId &&
                  !p.anioPartido && // Sin fecha cargada
                  !p.resultadoLocal &&
                  !p.resultadoVisitante // Sin resultado
              );

              if (partidoVueltaCreado) {
                partidosEnEdicionNuevos[partidoVueltaCreado.id] = { ...partidoVueltaCreado };
              }
            });

            if (Object.keys(partidosEnEdicionNuevos).length > 0) {
              setPartidosEnEdicion(partidosEnEdicionNuevos);
            }
          }
        });
      });
    }

    // Actualizar partidos editados con PATCH batch
    if (partidosEditadosValidos.length > 0) {
      const dtosEditados = partidosEditadosValidos.map((partido) => ({
        idPartido: partido.id,
        ...DTOs.PatchPartidoFixture(partido),
      }));

      // Guardar IDs de partidos editados para limpiarlos después
      const idsPartidosEditados = partidosEditadosValidos.map((p) => p.id);

      patchData(`torneos/${torneo.id}/partidos`, dtosEditados, (resultado) => {
        // Recargar después de actualizar
        setErroresFilaPartido((prev) => {
          const nuevos = { ...prev };
          idsPartidosEditados.forEach((id) => delete nuevos[id]);
          return nuevos;
        });
        recargarPartidos();
        // Limpiar solo los partidos que se actualizaron (no todos)
        setPartidosEnEdicion((prev) => {
          const nuevosEnEdicion = { ...prev };
          idsPartidosEditados.forEach((id) => {
            delete nuevosEnEdicion[id];
          });
          return nuevosEnEdicion;
        });
      });
    }
  };

  const recargarPartidos = () => {
    const url = `torneos/${torneo.id}/etapas/${etapa.etapa.codigo}/partidos`;
    fetchData(url, (partidos) => {
      const partidosMapeados = (partidos || []).map((p) => ({
        ...p,
        codigoInstancia: mapearInstanciaACodigo(p.instancia),
      }));
      setPartidosExistentes(partidosMapeados);
    });
  };

  // Separar partidos por instancia para cada lista
  const partidosExistentesPorInstancia = Funciones.AgruparPartidosPorInstancia(partidosExistentes);
  const partidosFaltantesPorInstancia = Funciones.AgruparPartidosPorInstancia(partidosFaltantes);
  const cantidadRuedas = etapa?.cantidadRuedas || 1;

  // Renderizar una fila de partido existente (solo lectura o editable)
  const renderFilaPartidoExistente = (partido, mostrarZona) => {
    const enEdicion = !!partidosEnEdicion[partido.id];

    if (enEdicion) {
      return (
        <FilaPartidoFixture
          key={`edit-${partido.id}`}
          partido={partidosEnEdicion[partido.id]}
          equipos={equiposDisponibles}
          torneo={torneo}
          mostrarEquipoLibre={false}
          mostrarZona={mostrarZona}
          esVuelta={false}
          isGrabando={partidoGrabandose === partido.id}
          onChange={(partidoActualizado) =>
            onPartidoEnEdicionChange(partido.id, partidoActualizado)
          }
          onGuardar={() => onGuardarEdicion(partidosEnEdicion[partido.id])}
          onCancelar={() => onCancelarEdicion(partido.id)}
          errores={erroresFilaPartido[partido.id] || []}
        />
      );
    }

    const resultado =
      partido.resultadoLocal != null && partido.resultadoVisitante != null
        ? `${partido.resultadoLocal} - ${partido.resultadoVisitante}`
        : '-';

    return (
      <tr key={partido.id}>
        <td className={styles.ordenPartido}>{partido.ordenPartido || ''}</td>
        <td className={styles.fecha}>
          {FuncionesFecha.CamposToFechaCorta(
            partido.anioPartido,
            partido.mesPartido,
            partido.diaPartido
          )}
        </td>
        <td className={styles.equipoLocal}>
          {FuncionesEquipo.NombreEquipoTorneo(partido.equipoLocal, torneo)}
        </td>
        <td className={styles.resultado}>{resultado}</td>
        <td className={styles.equipoVisitante}>
          {FuncionesEquipo.NombreEquipoTorneo(partido.equipoVisitante, torneo)}
        </td>
        {mostrarZona && <td className={styles.zona}>{partido.zona || ''}</td>}
        <ColumnaBotoneraItem
          idItem={partido.id}
          onEditarItem={onEditarPartido}
          onEliminarItem={onEliminarPartido}
          className={styles.botones}
        />
      </tr>
    );
  };

  // Si no es etapa de definición, no mostrar nada
  if (!etapa || etapa.definicion !== true) {
    return null;
  }

  return (
    <div className={styles.contenedor}>
      <h3>Fixture de {etapa.etapa?.descripcion || 'Etapa'}</h3>

      {cantidadRuedas === 1 && (
        <div className={styles.grupo}>
          <h4>Partidos Únicos</h4>
          <table className={styles.tabla}>
            <colgroup>
              <col className={styles.ordenPartido} />
              <col className={styles.fecha} />
              <col className={styles.equipoLocal} />
              <col className={styles.resultado} />
              <col className={styles.equipoVisitante} />
              {etapa.zonas && <col className={styles.zona} />}
              <col className={styles.botones} />
            </colgroup>
            <thead>
              <tr>
                <th className={styles.ordenPartido}>#</th>
                <th className={styles.fecha}>Día</th>
                <th className={styles.equipoLocal}>Local</th>
                <th className={styles.resultado}>Res</th>
                <th className={styles.equipoVisitante}>Visitante</th>
                {etapa.zonas && <th className={styles.zona}>Zona</th>}
                <th className={styles.botones}></th>
              </tr>
            </thead>
            <tbody>
              {/* Partidos existentes (solo lectura) */}
              {Funciones.OrdenarPartidos(partidosExistentesPorInstancia[Instancia.UNICO] || []).map(
                (partido) => renderFilaPartidoExistente(partido, etapa.zonas)
              )}

              {/* Partidos faltantes (editables) */}
              {puedeEditar &&
                Funciones.OrdenarPartidos(partidosFaltantesPorInstancia[Instancia.UNICO] || []).map(
                  (partido) => {
                    const indexEnFaltantes = partidosFaltantes.findIndex(
                      (p) => p.idPartido === partido.idPartido
                    );
                    return (
                      <FilaPartidoFixture
                        key={`faltante-${partido.indiceCruce}`}
                        partido={partido}
                        equipos={equiposDisponibles}
                        torneo={torneo}
                        mostrarEquipoLibre={false}
                        mostrarZona={etapa.zonas}
                        esVuelta={false}
                        isGrabando={partidoGrabandose === partido.idPartido}
                        onChange={(partidoActualizado) =>
                          onPartidoChange(indexEnFaltantes, partidoActualizado)
                        }
                        onGuardar={() => onGuardarPartido(partido)}
                        onCancelar={() => onCancelarPartido(partido)}
                        errores={erroresFilaPartido[partido.idPartido] || []}
                      />
                    );
                  }
                )}
            </tbody>
          </table>
        </div>
      )}

      {cantidadRuedas === 2 && (
        <React.Fragment>
          <div className={styles.grupo}>
            <h4>Ida</h4>
            <table className={styles.tabla}>
              <thead>
                <tr>
                  <th className={styles.ordenPartido}>#</th>
                  <th className={styles.fecha}>Día</th>
                  <th className={styles.equipoLocal}>Local</th>
                  <th className={styles.resultado}>Res</th>
                  <th className={styles.equipoVisitante}>Visitante</th>
                  {etapa.zonas && <th className={styles.zona}>Zona</th>}
                  <th className={styles.botones}></th>
                </tr>
              </thead>
              <tbody>
                {/* Partidos existentes (solo lectura) */}
                {Funciones.OrdenarPartidos(partidosExistentesPorInstancia[Instancia.IDA] || []).map(
                  (partido) => renderFilaPartidoExistente(partido, etapa.zonas)
                )}

                {/* Partidos faltantes (editables) */}
                {puedeEditar &&
                  Funciones.OrdenarPartidos(partidosFaltantesPorInstancia[Instancia.IDA] || []).map(
                    (partido) => {
                      const indexEnFaltantes = partidosFaltantes.findIndex(
                        (p) => p.idPartido === partido.idPartido
                      );
                      return (
                        <FilaPartidoFixture
                          key={`faltante-ida-${partido.indiceCruce}`}
                          partido={partido}
                          equipos={equiposDisponibles}
                          torneo={torneo}
                          mostrarEquipoLibre={false}
                          mostrarZona={etapa.zonas}
                          esVuelta={false}
                          isGrabando={partidoGrabandose === partido.idPartido}
                          onChange={(partidoActualizado) =>
                            onPartidoChange(indexEnFaltantes, partidoActualizado)
                          }
                          onGuardar={() => onGuardarPartido(partido)}
                          onCancelar={() => onCancelarPartido(partido)}
                          errores={erroresFilaPartido[partido.idPartido] || []}
                        />
                      );
                    }
                  )}
              </tbody>
            </table>
          </div>

          <div className={styles.grupo}>
            <h4>Vuelta</h4>
            <table className={styles.tabla}>
              <thead>
                <tr>
                  <th className={styles.ordenPartido}>#</th>
                  <th className={styles.fecha}>Día</th>
                  <th className={styles.equipoLocal}>Local (auto)</th>
                  <th className={styles.resultado}>Res</th>
                  <th className={styles.equipoVisitante}>Visitante (auto)</th>
                  {etapa.zonas && <th className={styles.zona}>Zona</th>}
                  <th className={styles.botones}></th>
                </tr>
              </thead>
              <tbody>
                {/* Partidos existentes (solo lectura) */}
                {Funciones.OrdenarPartidos(
                  partidosExistentesPorInstancia[Instancia.VUELTA] || []
                ).map((partido) => renderFilaPartidoExistente(partido, etapa.zonas))}

                {/* Partidos faltantes (editables) */}
                {puedeEditar &&
                  Funciones.OrdenarPartidos(
                    partidosFaltantesPorInstancia[Instancia.VUELTA] || []
                  ).map((partido) => {
                    const indexEnFaltantes = partidosFaltantes.findIndex(
                      (p) => p.idPartido === partido.idPartido
                    );
                    return (
                      <FilaPartidoFixture
                        key={`faltante-vuelta-${partido.indiceCruce}`}
                        partido={partido}
                        equipos={equiposDisponibles}
                        torneo={torneo}
                        mostrarEquipoLibre={false}
                        mostrarZona={etapa.zonas}
                        esVuelta={true} // Los equipos no se pueden editar, vienen de ida
                        isGrabando={partidoGrabandose === partido.idPartido}
                        onChange={(partidoActualizado) =>
                          onPartidoChange(indexEnFaltantes, partidoActualizado)
                        }
                        onGuardar={() => onGuardarPartido(partido)}
                        onCancelar={() => onCancelarPartido(partido)}
                        errores={erroresFilaPartido[partido.idPartido] || []}
                      />
                    );
                  })}
              </tbody>
            </table>
          </div>
        </React.Fragment>
      )}

      {puedeEditar &&
        (partidosFaltantes.length > 0 || Object.keys(partidosEnEdicion).length > 0) && (
          <div className={styles.acciones}>
            <button
              onClick={onGuardarTodos}
              disabled={isGrabando || isGrabandoPatch}
              className={styles.btnGuardarTodos}
            >
              Guardar todos los partidos editados
            </button>
            <MensajeGrabando
              isGrabando={isGrabando || isGrabandoPatch}
              entidad="partido"
              plural={true}
              estilo={styles.mensajeGrabandoTodos}
            />
          </div>
        )}

      <MensajeCargando isCargando={isCargando} />
      <MensajeError errores={erroresGetLista} />
      <MensajeError errores={erroresPost} />
      <MensajeError errores={erroresPatch} />
    </div>
  );
};

export default FixtureEtapaDefinicion;
